import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { LogIn, LogOut, PenLine, Timer, Coffee, UserX, Briefcase, ListChecks, Route } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { fmtFecha, fmtFechaLargaReloj, fmtHora, fmtHoraReloj, fmtHoras, offsetDesdeHoraServidor } from "@/lib/format";
import { PAUSA_LABEL } from "@/lib/fichajeUi";
import { useAuth } from "@/context/AuthContext";
import { useEmpresa } from "@/hooks/useEmpresa";
import EmpresaBrand from "@/components/EmpresaBrand";
import JornadaHoySubnivel from "@/components/JornadaHoySubnivel";
import SolicitudPicadoManualDialog from "@/components/SolicitudPicadoManualDialog";
import AvisoCumpleanosDialog from "@/components/AvisoCumpleanosDialog";
import NotificacionesPendientesSimple from "@/components/NotificacionesPendientesSimple";
import FestivosEmpresaPanel from "@/components/FestivosEmpresaPanel";
import MotivoAusenciaJustificadaFields from "@/components/MotivoAusenciaJustificadaFields";
import ProyectoImputacionPanel from "@/components/ProyectoImputacionPanel";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ficharEntrarButtonStyle, ficharLabelColor, ficharSurfaceStyle } from "@/lib/uiTheme";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";

const MOTIVOS_PAUSA = [
  { id: "cafe", label: "Pausa café" },
  { id: "comida", label: "Comida" },
  { id: "personal", label: "Personal" },
  { id: "fumar", label: "Fumar" },
  { id: "otro", label: "Otro" },
];

const fmtHorasDecimal = (valor) =>
  `${Number(valor || 0).toLocaleString("es-ES", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })} h`;

export default function Fichar() {
  const { user } = useAuth();
  const { empresa } = useEmpresa();
  const [estado, setEstado] = useState(null);
  const [reloj, setReloj] = useState(() => fmtHoraReloj(0));
  const [fechaLarga, setFechaLarga] = useState(() => fmtFechaLargaReloj(0));
  const [enviando, setEnviando] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [mostrarMotivosPausa, setMostrarMotivosPausa] = useState(false);
  const [confirmAusencia, setConfirmAusencia] = useState(false);
  const [alcanceAusencia, setAlcanceAusencia] = useState("tramo");
  const [motivoAusencia, setMotivoAusencia] = useState("");
  const [avisoEmergente, setAvisoEmergente] = useState(null);
  const [elegirProyecto, setElegirProyecto] = useState(false);
  const [cambiarProyecto, setCambiarProyecto] = useState(false);
  const [cambiarTarea, setCambiarTarea] = useState(false);
  const [rutaHoy, setRutaHoy] = useState(null);
  const fetchedAt = useRef(Date.now());
  const serverOffsetMs = useRef(0);
  const avisoMostradoRef = useRef(null);

  const sincronizarRelojServidor = useCallback((data) => {
    serverOffsetMs.current = offsetDesdeHoraServidor(data?.hora_servidor);
    setReloj(fmtHoraReloj(serverOffsetMs.current));
    setFechaLarga(fmtFechaLargaReloj(serverOffsetMs.current));
  }, []);

  const aplicarEstado = useCallback((data) => {
    setEstado(data);
    fetchedAt.current = Date.now();
    sincronizarRelojServidor(data);
  }, [sincronizarRelojServidor]);

  const mostrarAvisoSiCorresponde = useCallback((data) => {
    const aviso = data?.aviso_emergente;
    if (!aviso || avisoMostradoRef.current === aviso.notif_id) return;
    avisoMostradoRef.current = aviso.notif_id;
    setAvisoEmergente(aviso);
  }, []);

  const cerrarAvisoEmergente = async () => {
    const id = avisoEmergente?.notif_id;
    setAvisoEmergente(null);
    avisoMostradoRef.current = null;
    if (id) {
      try {
        await api.put(`/notificaciones/${encodeURIComponent(id)}/leida`);
      } catch {
        /* el aviso seguirá en la lista hasta marcarlo manualmente */
      }
    }
  };

  const cargar = useCallback(async () => {
    try {
      const { data } = await api.get("/fichajes/estado");
      let proyectos = data.proyectos_asignados || [];
      if (!data.dentro && !data.en_pausa && !proyectos.length) {
        try {
          const r = await api.get("/proyectos/mis/asignaciones", { params: { para_fichaje: true } });
          proyectos = r.data?.items || [];
        } catch {
          /* módulo de proyectos no activo o sin permiso */
        }
      }
      const estadoActualizado = { ...data, proyectos_asignados: proyectos };
      aplicarEstado(estadoActualizado);
      mostrarAvisoSiCorresponde(estadoActualizado);
      if (data.dentro) setElegirProyecto(false);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }, [aplicarEstado, mostrarAvisoSiCorresponde]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  useEffect(() => {
    const t = setInterval(() => {
      setReloj(fmtHoraReloj(serverOffsetMs.current));
      setFechaLarga(fmtFechaLargaReloj(serverOffsetMs.current));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const tickSeg = estado?.dentro ? Math.floor((Date.now() - fetchedAt.current) / 1000) : 0;
  const segundosFichajesVivos = estado
    ? (estado.segundos_fichajes ?? estado.segundos_hoy ?? 0) + tickSeg
    : 0;
  const segundosPendienteAj = estado?.segundos_ausencia_pendiente || 0;
  const segundosHoy = estado
    ? (estado.segundos_hoy || 0) + tickSeg
    : 0;

  const segundosPausa = estado?.pausa_activa
    ? estado.pausa_activa.segundos + Math.floor((Date.now() - fetchedAt.current) / 1000)
    : 0;

  const getCoords = () =>
    new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      navigator.geolocation.getCurrentPosition(
        (pos) => resolve({ latitud: pos.coords.latitude, longitud: pos.coords.longitude }),
        () => resolve(null),
        { timeout: 4000, maximumAge: 60000 }
      );
    });

  const ejecutarFichaje = async (imputacion = null) => {
    setEnviando(true);
    try {
      const coords = await getCoords();
      const payload = { ...(coords || {}) };
      if (!estado?.dentro && imputacion) {
        Object.assign(payload, imputacion);
      }
      const { data } = await api.post("/fichajes/fichar", payload);
      aplicarEstado(data);
      mostrarAvisoSiCorresponde(data);
      setElegirProyecto(false);
      const conGps = coords ? " (con ubicación)" : "";
      toast.success(data.dentro ? `Entrada registrada${conGps}. ¡Buen trabajo!` : `Salida registrada${conGps}. ¡Hasta pronto!`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviando(false);
    }
  };

  const onEntrarClick = () => {
    if ((estado?.proyectos_asignados || []).length > 0) {
      setElegirProyecto(true);
      return;
    }
    ejecutarFichaje();
  };

  const cambiarImputacion = async (imputacion, tipo = "proyecto") => {
    if (!imputacion) return;
    setEnviando(true);
    try {
      const coords = await getCoords();
      const { data } = await api.post("/fichajes/cambiar-proyecto", { ...imputacion, ...(coords || {}) });
      aplicarEstado(data);
      setCambiarProyecto(false);
      setCambiarTarea(false);
      toast.success(tipo === "tarea" ? "Tarea actualizada" : "Proyecto actualizado");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviando(false);
    }
  };

  const registrarAusencia = async () => {
    setEnviando(true);
    try {
      const { data } = await api.post("/fichajes/ausencia", {
        alcance: alcanceAusencia,
        motivo: motivoAusencia.trim(),
      });
      aplicarEstado(data);
      mostrarAvisoSiCorresponde(data);
      setConfirmAusencia(false);
      setMotivoAusencia("");
      toast.success(
        data?.ausencia_solicitud?.jornada_cerrada
          ? "Solicitud enviada. Has salido de jornada a la hora de la solicitud."
          : "Solicitud enviada al encargado. Te avisaremos cuando la revise."
      );
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviando(false);
    }
  };

  const iniciarPausa = async (motivo) => {
    setEnviando(true);
    try {
      const { data } = await api.post("/fichajes/pausa", { motivo });
      aplicarEstado(data);
      mostrarAvisoSiCorresponde(data);
      setMostrarMotivosPausa(false);
      toast.success(`Pausa registrada (${PAUSA_LABEL[motivo] || motivo})`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviando(false);
    }
  };

  const volverDePausa = async () => {
    setEnviando(true);
    try {
      const coords = await getCoords();
      const { data } = await api.post("/fichajes/vuelta", coords || {});
      aplicarEstado(data);
      mostrarAvisoSiCorresponde(data);
      toast.success("Vuelta registrada. ¡A seguir!");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEnviando(false);
    }
  };

  useEffect(() => {
    if (!empresa?.gestionar_rutas_comerciales) {
      setRutaHoy(null);
      return;
    }
    api.get("/rutas-comerciales/hoy")
      .then((r) => setRutaHoy(r.data?.ruta || null))
      .catch(() => setRutaHoy(null));
  }, [empresa?.gestionar_rutas_comerciales]);

  const trabajando = estado?.dentro;
  const enPausa = estado?.en_pausa;
  const jornadaCompletaPendiente = estado?.ausencia_pendiente?.alcance === "dia_completo";
  const jornada = estado?.horario_jornada;
  const limitePausa = estado?.pausa_cafe_minutos ?? 15;
  const pausaExcedida = enPausa && segundosPausa > limitePausa * 60;
  const picadosPendientes = estado?.picados_pendientes || [];
  const proyectosAsignados = estado?.proyectos_asignados || [];
  const proyectoActual = estado?.proyecto_actual;
  const computoFlexible = estado?.computo_flexible;
  const horasFlexiblesTrabajadas = computoFlexible
    ? (Number(computoFlexible.horas_trabajadas) || 0)
      + (trabajando ? Math.floor((Date.now() - fetchedAt.current) / 1000) / 3600 : 0)
    : 0;
  const horasFlexiblesObjetivo = Number(computoFlexible?.horas_objetivo) || 0;
  const horasFlexiblesExtra = Math.max(0, horasFlexiblesTrabajadas - horasFlexiblesObjetivo);
  const horasFlexiblesPendientes = Math.max(0, horasFlexiblesObjetivo - horasFlexiblesTrabajadas);
  const progresoFlexible = horasFlexiblesObjetivo
    ? Math.min(100, (horasFlexiblesTrabajadas / horasFlexiblesObjetivo) * 100)
    : 0;

  return (
    <div className="space-y-5" data-testid="fichar-page">
      {empresa && user && (
        <EmpresaBrand
          empresa={empresa}
          hideNombre
          usuario={user}
          subtitle={`${user.nombre} ${user.apellidos}${user.departamento ? ` · ${user.departamento}` : ""}`}
          detalle={<JornadaHoySubnivel jornada={jornada} />}
        />
      )}

      {picadosPendientes.length > 0 && (
        <div className="rounded-xl border border-amber-200 bg-amber-50/80 px-4 py-3 text-sm text-amber-950">
          <p className="font-medium">Tienes {picadosPendientes.length} picado(s) por regularizar hoy</p>
          <p className="mt-1 text-xs text-amber-800/80">
            Ve a{" "}
            <Link to="/mis-fichajes" className="font-medium underline">Picados</Link>
            {" "}para revisarlos, añadirlos o corregirlos.
          </p>
        </div>
      )}

      {estado?.solicitudes_fichaje_pendientes?.length > 0 && trabajando && (
        <div className="rounded-xl border border-sky-200 bg-sky-50/80 px-4 py-3 text-xs text-sky-900">
          Tienes picados en revisión, pero puedes usar <strong>Salir</strong> o <strong>Pausa</strong> con normalidad.
          Para solicitar la salida manual, ve a{" "}
          <Link to="/mis-fichajes" className="font-medium underline">Picados</Link>.
        </div>
      )}

      <div
        className={`relative overflow-hidden rounded-3xl border p-6 shadow-sm ${
        trabajando ? "ring-2 ring-emerald-200/60" : enPausa ? "ring-2 ring-amber-200/60" : ""
      }`}
        style={ficharSurfaceStyle()}
      >
        <p className="relative text-center text-xs font-semibold uppercase tracking-[0.12em]" style={ficharLabelColor()}>
          {fechaLarga.charAt(0).toUpperCase() + fechaLarga.slice(1)}
        </p>
        <p className="font-display relative mt-2 text-center text-5xl font-light tracking-tight text-zinc-900 sm:text-6xl" data-testid="reloj-actual">
          {reloj}
        </p>

        {estado && (
          <div className="relative mt-4 flex flex-col items-center gap-3">
            <span
              className={`inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm font-medium shadow-sm ${
                trabajando
                  ? "bg-emerald-500 text-white"
                  : enPausa
                    ? "bg-amber-500 text-white"
                    : estado?.ausencia_pendiente
                      ? "bg-violet-600 text-white"
                      : "bg-white text-zinc-600 ring-1 ring-zinc-200"
              }`}
              data-testid="estado-texto"
            >
              <span
                className={`h-2 w-2 rounded-full ${
                  trabajando || enPausa || estado?.ausencia_pendiente ? "animate-pulse bg-white" : "bg-zinc-400"
                }`}
              />
              {enPausa
                ? `En pausa${
                    estado.pausa_activa?.motivo
                      ? ` (${PAUSA_LABEL[estado.pausa_activa.motivo] || estado.pausa_activa.motivo})`
                      : ""
                  }`
                : trabajando
                  ? `Trabajando desde las ${fmtHora(estado.hora_entrada)}`
                  : estado?.ausencia_pendiente
                  ? `Ausencia justificada pendiente${
                      estado.ausencia_pendiente.alcance === "dia_completo"
                        ? " · día completo (no hace falta volver a picar)"
                        : estado.ausencia_pendiente.hora_solicitada
                        ? ` · desde ${fmtHora(estado.ausencia_pendiente.hora_solicitada)}`
                        : ""
                    }`
                  : estado.segundos_hoy > 0
                    ? `Sin fichar ahora · ${fmtHoras(segundosHoy)} hoy`
                    : "Sin fichar"}
            </span>
            {trabajando && estado?.ausencia_pendiente && (
              <p className="text-[11px] text-violet-700" data-testid="aviso-ausencia-pendiente">
                Ausencia justificada pendiente de autorización
              </p>
            )}

            {trabajando && proyectoActual && (
              <div
                className="w-full max-w-sm rounded-2xl border border-emerald-200 bg-emerald-50/80 px-4 py-3 text-left shadow-sm"
                data-testid="proyecto-actual-card"
              >
                <div className="flex items-start gap-3">
                  <span
                    className="mt-0.5 h-8 w-1.5 shrink-0 rounded-full"
                    style={{ backgroundColor: proyectoActual.color || "#10b981" }}
                  />
                  <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-semibold uppercase tracking-wide text-emerald-800/70">
                      {proyectoActual.es_ruta ? "Ruta actual" : "Proyecto actual"}
                    </p>
                    <p className="truncate text-sm font-semibold text-emerald-950">
                      {proyectoActual.codigo ? `${proyectoActual.codigo} · ` : ""}
                      {proyectoActual.nombre}
                    </p>
                    {proyectoActual.tarea_nombre ? (
                      <p className="mt-1 truncate text-xs text-emerald-900/80">
                        <span className="font-medium">Tarea:</span>{" "}
                        {proyectoActual.tarea_codigo ? `${proyectoActual.tarea_codigo} · ` : ""}
                        {proyectoActual.tarea_nombre}
                        {proyectoActual.facturable === false ? " · no facturable" : ""}
                      </p>
                    ) : (
                      <p className="mt-1 text-xs text-emerald-800/60">Sin tarea / fase seleccionada</p>
                    )}
                  </div>
                </div>
              </div>
            )}
            {trabajando && !proyectoActual && proyectosAsignados.length > 0 && (
              <p className="text-center text-xs text-zinc-500">
                Entraste sin proyecto. Puedes asignar uno con «Cambiar proyecto».
              </p>
            )}
          </div>
        )}

        {rutaHoy && (
          <Link
            to="/mis-rutas"
            className="mx-auto mt-4 flex w-full max-w-sm items-center gap-3 rounded-2xl border border-rose-200 bg-gradient-to-r from-rose-50 to-orange-50 px-4 py-3 text-left shadow-sm transition hover:border-rose-300"
          >
            <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-rose-600 text-white">
              <Route className="h-5 w-5" />
            </span>
            <div className="min-w-0 flex-1">
              <p className="text-[10px] font-semibold uppercase tracking-wide text-rose-800/70">Ruta comercial de hoy</p>
              <p className="truncate text-sm font-semibold text-rose-950">{rutaHoy.titulo}</p>
              <p className="text-xs text-rose-800/80">
                {rutaHoy.paradas_visitadas}/{rutaHoy.num_paradas} visitas · Ver planning
              </p>
            </div>
          </Link>
        )}

        {enPausa && (
          <div className="relative mx-auto mt-3 text-center">
            <p className="text-sm text-amber-800">
              Pausa: {fmtHoras(segundosPausa)}
              {pausaExcedida && (
                <span className="ml-1 text-rose-600">(exceso no trabajado)</span>
              )}
            </p>
            <p className="mt-0.5 text-[11px] text-amber-700/70">
              Los primeros {limitePausa} min cuentan como trabajados
            </p>
          </div>
        )}

        <div className="relative mt-8 flex flex-col items-center gap-4">
          {enPausa ? (
            <button
              onClick={volverDePausa}
              disabled={enviando || !estado}
              data-testid="vuelta-pausa-btn"
              className="relative flex h-40 w-40 flex-col items-center justify-center gap-2 rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-white shadow-lg ring-4 ring-sky-100 transition-all active:scale-95 disabled:opacity-60 sm:h-44 sm:w-44"
            >
              <LogIn className="relative h-9 w-9" strokeWidth={1.5} />
              <span className="font-display relative text-xl font-light">Volver de pausa</span>
            </button>
          ) : trabajando ? (
            mostrarMotivosPausa ? (
              <div className="w-full max-w-sm rounded-2xl border border-amber-200 bg-white p-4 shadow-md" data-testid="pausa-motivos-panel">
                <p className="text-center text-sm font-semibold text-amber-900">Selecciona el motivo de la pausa</p>
                <p className="mt-1 text-center text-xs text-zinc-500">
                  Se registrará como pausa (no salida). Hasta {limitePausa} min cuentan como trabajados.
                </p>
                <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                  {(estado?.pausa_motivos?.length ? estado.pausa_motivos : MOTIVOS_PAUSA).map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      disabled={enviando}
                      data-testid={`pausa-motivo-${m.id}`}
                      onClick={() => iniciarPausa(m.id)}
                      className="rounded-xl border border-amber-200 bg-amber-50 px-4 py-3 text-left text-sm font-medium text-amber-900 transition-colors hover:bg-amber-100 active:scale-[0.98] disabled:opacity-60"
                    >
                      {m.label}
                    </button>
                  ))}
                </div>
                <Button
                  variant="ghost"
                  className="mt-3 w-full text-zinc-500"
                  onClick={() => setMostrarMotivosPausa(false)}
                >
                  Cancelar
                </Button>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-4">
                <div className="flex flex-col items-center gap-4 sm:flex-row">
                  <button
                    onClick={() => setMostrarMotivosPausa(true)}
                    disabled={enviando || !estado}
                    data-testid="iniciar-pausa-btn"
                    className="relative flex h-32 w-32 flex-col items-center justify-center gap-2 rounded-full bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg ring-4 ring-amber-100 transition-all active:scale-95 disabled:opacity-60"
                  >
                    <Coffee className="relative h-8 w-8" strokeWidth={1.5} />
                    <span className="font-display relative text-lg font-light">Pausa</span>
                  </button>
                  <button
                    onClick={() => ejecutarFichaje()}
                    disabled={enviando || !estado}
                    data-testid="fichar-btn"
                    className="relative flex h-32 w-32 flex-col items-center justify-center gap-2 rounded-full bg-gradient-to-br from-rose-500 to-orange-500 text-white shadow-lg ring-4 ring-rose-100 transition-all active:scale-95 disabled:opacity-60"
                  >
                    <LogOut className="relative h-8 w-8" strokeWidth={1.5} />
                    <span className="font-display relative text-lg font-light">Salir</span>
                  </button>
                </div>
                <button
                  type="button"
                  onClick={() => setConfirmAusencia(true)}
                  disabled={enviando || !estado}
                  className="inline-flex items-center gap-2 rounded-xl border border-violet-200 bg-violet-50 px-4 py-2 text-sm font-medium text-violet-800 hover:bg-violet-100 disabled:opacity-60"
                  data-testid="ausencia-btn"
                >
                  <UserX className="h-4 w-4" />
                  Ausencia justificada
                </button>
                {proyectosAsignados.length > 0 && (
                  <div className="flex flex-wrap items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      onClick={() => {
                        setCambiarTarea(false);
                        setCambiarProyecto(true);
                      }}
                      disabled={enviando}
                      data-testid="cambiar-proyecto-btn"
                    >
                      <Briefcase className="mr-2 h-4 w-4" /> {proyectosAsignados.some((p) => p.es_ruta) ? "Cambiar proyecto o ruta" : "Cambiar proyecto"}
                    </Button>
                    {proyectoActual &&
                      !proyectoActual.es_ruta &&
                      (proyectosAsignados.find((p) => String(p.id) === String(proyectoActual.id))?.tareas || []).length > 0 && (
                        <Button
                          variant="outline"
                          onClick={() => {
                            setCambiarProyecto(false);
                            setCambiarTarea(true);
                          }}
                          disabled={enviando}
                          data-testid="cambiar-tarea-btn"
                        >
                          <ListChecks className="mr-2 h-4 w-4" /> Cambiar tarea
                        </Button>
                      )}
                  </div>
                )}
                {cambiarProyecto && (
                  <ProyectoImputacionPanel
                    proyectos={proyectosAsignados}
                    enviando={enviando}
                    modo="cambio"
                    onConfirm={(imp) => cambiarImputacion(imp, "proyecto")}
                    onCancel={() => setCambiarProyecto(false)}
                  />
                )}
                {cambiarTarea && proyectoActual && (
                  <ProyectoImputacionPanel
                    proyectos={proyectosAsignados}
                    enviando={enviando}
                    modo="tarea"
                    proyectoFijoId={proyectoActual.id}
                    tareaActualId={proyectoActual.tarea_id}
                    facturableActual={proyectoActual.facturable}
                    onConfirm={(imp) => cambiarImputacion(imp, "tarea")}
                    onCancel={() => setCambiarTarea(false)}
                  />
                )}
              </div>
            )
          ) : elegirProyecto ? (
            <ProyectoImputacionPanel proyectos={proyectosAsignados} enviando={enviando} onConfirm={ejecutarFichaje} onCancel={() => setElegirProyecto(false)} />
          ) : manualOpen ? (
            <SolicitudPicadoManualDialog
              estado={estado}
              open={manualOpen}
              onOpenChange={setManualOpen}
              onSuccess={cargar}
              presentacion="inline"
            />
          ) : (
            <button
              type="button"
              onClick={onEntrarClick}
              disabled={enviando || !estado || jornadaCompletaPendiente}
              data-testid="fichar-btn"
              className="relative flex h-40 w-40 flex-col items-center justify-center gap-2 rounded-full text-white shadow-lg transition-all duration-300 active:scale-95 disabled:opacity-60 sm:h-44 sm:w-44"
              style={ficharEntrarButtonStyle()}
            >
              <span className="absolute inset-0 rounded-full animate-ping opacity-20 bg-white" style={{ animationDuration: "2.5s" }} />
              <LogIn className="relative h-9 w-9" strokeWidth={1.5} />
              <span className="font-display relative text-2xl font-light">
                {jornadaCompletaPendiente ? "Día cubierto" : "Entrar"}
              </span>
            </button>
          )}
        </div>
      </div>

      <NotificacionesPendientesSimple />

      <FestivosEmpresaPanel compact className="mt-4" />

      <div className="overflow-hidden rounded-2xl border border-indigo-100/90 bg-[#eef0fa] shadow-sm">
        <div className="flex items-center justify-between border-b border-indigo-100/80 bg-white/60 px-5 py-4">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-sky-500 to-blue-600 text-white shadow-sm">
              <Timer className="h-5 w-5" strokeWidth={1.5} />
            </div>
            <div>
              <p className="text-xs font-semibold uppercase tracking-[0.1em] text-sky-700/80">Horas hoy</p>
              <p className="font-display text-3xl font-light tracking-tight text-zinc-900" data-testid="horas-hoy">
                {fmtHoras(segundosHoy)}
              </p>
              {segundosPendienteAj > 0 && (
                <p className="mt-0.5 text-[11px] leading-snug text-violet-700" data-testid="horas-hoy-desglose">
                  {fmtHoras(segundosFichajesVivos)} trabajadas + {fmtHoras(segundosPendienteAj)} pendientes de
                  autorización = {fmtHoras(segundosHoy)}
                </p>
              )}
              {estado?.segundos_pausa_exceso > 0 && (
                <p className="text-[11px] text-rose-600">
                  −{fmtHoras(estado.segundos_pausa_exceso)} pausa no trabajada
                </p>
              )}
            </div>
          </div>
          <Button
            type="button"
            variant="outline"
            size="sm"
            data-testid="abrir-manual-horas-btn"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setManualOpen(true);
              window.scrollTo({ top: 0, behavior: "smooth" });
            }}
            className="shrink-0 gap-1.5 rounded-lg border-amber-200 bg-amber-50 text-amber-800 hover:bg-amber-100"
          >
            <PenLine className="h-3.5 w-3.5" strokeWidth={1.5} />
            <span className="hidden sm:inline">Picado manual</span>
            <span className="sm:hidden">Manual</span>
          </Button>
        </div>

        <div className="px-5 py-4">
          {computoFlexible && (
            <div className="mb-4 rounded-xl border border-emerald-100 bg-white/75 p-4 shadow-sm" data-testid="computo-flexible-panel">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.1em] text-emerald-700">
                    Cómputo flexible · {computoFlexible.etiqueta_periodo || computoFlexible.computo}
                  </p>
                  <p className="mt-1 text-xs text-zinc-500">
                    Del {fmtFecha(computoFlexible.periodo_inicio)} al {fmtFecha(computoFlexible.periodo_fin)}
                  </p>
                </div>
                <Badge variant="outline" className="border-emerald-200 bg-emerald-50 text-emerald-700">
                  Objetivo {fmtHorasDecimal(horasFlexiblesObjetivo)}
                </Badge>
              </div>

              <div className="mt-4 h-2 overflow-hidden rounded-full bg-emerald-100">
                <div
                  className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 transition-all"
                  style={{ width: `${progresoFlexible}%` }}
                />
              </div>

              <div className="mt-4 grid grid-cols-3 gap-2 text-center">
                <div className="rounded-lg bg-emerald-50 px-2 py-2">
                  <p className="text-[11px] text-emerald-700">Acumuladas</p>
                  <p className="font-display text-lg font-light text-zinc-900">
                    {fmtHorasDecimal(horasFlexiblesTrabajadas)}
                  </p>
                </div>
                <div className="rounded-lg bg-sky-50 px-2 py-2">
                  <p className="text-[11px] text-sky-700">Pendientes</p>
                  <p className="font-display text-lg font-light text-zinc-900">
                    {fmtHorasDecimal(horasFlexiblesPendientes)}
                  </p>
                </div>
                <div className="rounded-lg bg-amber-50 px-2 py-2">
                  <p className="text-[11px] text-amber-700">Extras</p>
                  <p className="font-display text-lg font-light text-zinc-900">
                    {fmtHorasDecimal(horasFlexiblesExtra)}
                  </p>
                </div>
              </div>
              {horasFlexiblesExtra > 0 && (
                <p className="mt-3 text-[11px] leading-snug text-amber-800/90">
                  El exceso sobre el objetivo requiere autorización (Mis solicitudes → Autorizar horas extra)
                  para ser compensable. Hasta entonces cuenta en el cómputo, pero no en la bolsa de HE.
                </p>
              )}
            </div>
          )}

          <Link
            to="/mis-fichajes"
            className="flex items-center justify-between rounded-xl border border-indigo-100 bg-white/70 px-4 py-3 text-sm text-indigo-700 transition-colors hover:bg-white"
            data-testid="ir-mis-picados-link"
          >
            <span>Revisar, corregir o añadir picados</span>
            <span className="text-xs font-medium">Picados →</span>
          </Link>
        </div>
      </div>

      <SolicitudPicadoManualDialog
        estado={estado}
        open={manualOpen && (trabajando || enPausa)}
        onOpenChange={setManualOpen}
        onSuccess={cargar}
        presentacion="sheet"
      />

      <AvisoCumpleanosDialog aviso={avisoEmergente} onCerrar={cerrarAvisoEmergente} />

      <AlertDialog open={confirmAusencia} onOpenChange={setConfirmAusencia}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Ausencia justificada</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-4 text-left text-sm text-zinc-600">
                <p>
                  Al enviar la solicitud se registra la hora actual y sales de jornada.
                  Si eliges tramo, se acota con tu entrada (o con el cierre automático si no vuelves).
                  Día completo cubre todos los turnos y no requiere picado de vuelta.
                </p>
                <fieldset className="space-y-2">
                  <legend className="text-xs font-semibold uppercase tracking-wide text-zinc-500">Alcance</legend>
                  <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-zinc-200 p-3 has-[:checked]:border-violet-400 has-[:checked]:bg-violet-50">
                    <input
                      type="radio"
                      name="alcance-ausencia"
                      value="tramo"
                      checked={alcanceAusencia === "tramo"}
                      onChange={() => setAlcanceAusencia("tramo")}
                      className="mt-0.5"
                      data-testid="ausencia-alcance-tramo"
                    />
                    <span>
                      <span className="font-medium text-zinc-900">Solo el tramo actual</span>
                      <span className="mt-0.5 block text-xs text-zinc-500">
                        Desde tu entrada hasta ahora. Si no vuelves, se cierra al fin del tramo (cierre automático).
                      </span>
                    </span>
                  </label>
                  <label className="flex cursor-pointer items-start gap-2 rounded-lg border border-zinc-200 p-3 has-[:checked]:border-violet-400 has-[:checked]:bg-violet-50">
                    <input
                      type="radio"
                      name="alcance-ausencia"
                      value="dia_completo"
                      checked={alcanceAusencia === "dia_completo"}
                      onChange={() => setAlcanceAusencia("dia_completo")}
                      className="mt-0.5"
                      data-testid="ausencia-alcance-dia"
                    />
                    <span>
                      <span className="font-medium text-zinc-900">Día completo</span>
                      <span className="mt-0.5 block text-xs text-zinc-500">
                        Todos los turnos del día. No hace falta picar la vuelta: se completa la jornada.
                      </span>
                    </span>
                  </label>
                </fieldset>
                <MotivoAusenciaJustificadaFields
                  value={motivoAusencia}
                  onChange={setMotivoAusencia}
                  label="Motivo"
                  requiredHint={false}
                />
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={enviando}>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={registrarAusencia} disabled={enviando} data-testid="ausencia-confirmar-btn">
              Enviar solicitud
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
